import { usePageRefresh } from '../../../context/PageRefreshContext';
import { localDateString } from '../../../utils/helpers/localDate';
import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getProjectsList, createProject, updateProject, deleteProject,
} from '../../../redux/actions/SocietyPortal/TaskAction/TaskAction';
import { getEmployeesDropdown } from '../../../redux/actions/SocietyPortal/EmployeeAction/EmployeeAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import SearchableDropdown from '../../../components/SearchableDropdown';
import TaskTabs from './TaskTabs';
import {
    PROJECT_STATUSES, projectStatusBadgeClass, humanize,
} from '../../../consts/taskConstants';
import { formatIstDate } from '../../../utils/helpers/istDate';

const statusOptions = PROJECT_STATUSES.map((s) => ({ value: s, label: humanize(s) }));

const fmtDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return formatIstDate(d);
};
const toDateInput = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return localDateString(d);
};

const EMPTY = { name: '', description: '', status: 'ACTIVE', start_date: '', due_date: '', employee_id: '' };
const inputCls = 'tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 tw-bg-white';
const labelCls = 'tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1';

const Projects = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { can } = usePermissions();
    const societyId = selectedSocietyStorer.getSocietyId();

    const {
        projectsListData, projectsListLoading,
        createProjectLoading, updateProjectLoading, deleteProjectLoading,
    } = useSelector((s) => s.TaskReducer);
    const { employeesDropdownData, employeesDropdownLoading } = useSelector((s) => s.EmployeeReducer);

    // Employee options from /api/employees/dropdown. Value is employee_id,
    // which is what the project API expects. Label with name (— type when available).
    const employeeOptions = useMemo(() => {
        const list = Array.isArray(employeesDropdownData) ? employeesDropdownData : [];
        return list.map((emp) => {
            const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
            return {
                value: String(emp.employee_id),
                label: emp.employee_type ? `${name} — ${emp.employee_type}` : name,
            };
        });
    }, [employeesDropdownData]);

    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('projectsViewMode') || 'card');
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [attempted, setAttempted] = useState(false);
    const [toDelete, setToDelete] = useState(null);

    const canManage = can(PERMISSIONS.TASK_MANAGE_PROJECTS);

    const fetchProjects = () => dispatch(getProjectsList({ search: search || undefined, status: filterStatus || undefined }));
    usePageRefresh(fetchProjects);

    useEffect(() => {
        fetchProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, search, filterStatus]);

    useEffect(() => {
        dispatch(getEmployeesDropdown({ societyId }));
    }, [dispatch, societyId]);

    const projects = Array.isArray(projectsListData) ? projectsListData : [];

    const openCreate = () => { setEditing(null); setForm(EMPTY); setAttempted(false); setModalOpen(true); };
    const openEdit = (p) => {
        setEditing(p);
        setForm({
            name: p.name || '',
            description: p.description || '',
            status: p.status || 'ACTIVE',
            start_date: toDateInput(p.start_date),
            due_date: toDateInput(p.due_date),
            employee_id: p.employee_id || '',
        });
        setAttempted(false);
        setModalOpen(true);
    };

    const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

    const changeView = (mode) => { setViewMode(mode); localStorage.setItem('projectsViewMode', mode); };

    const handleSave = () => {
        setAttempted(true);
        if (!form.name.trim()) return;
        const num = (v) => (v === '' || v == null ? undefined : Number(v));
        const payload = {
            name: form.name.trim(),
            description: form.description || undefined,
            status: form.status || undefined,
            start_date: form.start_date ? new Date(form.start_date).toISOString() : undefined,
            due_date: form.due_date ? new Date(form.due_date).toISOString() : undefined,
            employee_id: num(form.employee_id),
        };
        Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
        const onSuccess = () => { setModalOpen(false); fetchProjects(); };
        if (editing) dispatch(updateProject({ projectId: editing.project_id, ...payload, onSuccess }));
        else dispatch(createProject({ ...payload, onSuccess }));
    };

    const handleDelete = () => {
        if (!toDelete) return;
        dispatch(deleteProject({ projectId: toDelete.project_id, onSuccess: () => { setToDelete(null); fetchProjects(); } }));
    };

    const saving = createProjectLoading || updateProjectLoading;

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div className="tw-flex tw-items-center tw-gap-2">
                    <button onClick={() => navigate('/society-portal/tasks')} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500">
                        <Icon icon="mdi:arrow-left" />
                    </button>
                    <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-gradient-to-br tw-from-blue-500 tw-to-indigo-500 tw-flex tw-items-center tw-justify-center tw-shadow-sm">
                        <Icon icon="hugeicons:folder-01" className="tw-text-white tw-text-lg" />
                    </div>
                    <div>
                        <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800 tw-flex tw-items-center tw-gap-2">
                            Projects
                            {!projectsListLoading && <span className="tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-gray-100 tw-text-gray-600 tw-text-xs tw-font-medium">{projects.length}</span>}
                        </h1>
                        <p className="tw-text-xs tw-text-gray-500">Group related tasks into projects</p>
                    </div>
                </div>
                {canManage && (
                    <button onClick={openCreate} className="tw-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700">
                        <Icon icon="mdi:plus" /> New Project
                    </button>
                )}
            </div>

            <TaskTabs active="projects" />

            {/* Filter bar */}
            <div className="tw-bg-white tw-rounded-lg tw-py-2.5 tw-px-3 tw-mb-4 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
                <div className="tw-flex-1 tw-min-w-[200px] tw-relative">
                    <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                    <input type="text" placeholder="Search projects..." value={search} onChange={(e) => setSearch(e.target.value)}
                        className="tw-w-full tw-pl-8 tw-pr-2 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500" />
                </div>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                    className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500">
                    <option value="">All Status</option>
                    {PROJECT_STATUSES.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
                </select>
                {/* View toggle */}
                <div className="tw-flex tw-items-center tw-gap-0.5 tw-p-0.5 tw-bg-gray-100 tw-rounded-md tw-flex-shrink-0">
                    <button onClick={() => changeView('card')} title="Card view"
                        className={`tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded ${viewMode === 'card' ? 'tw-bg-white tw-text-blue-600 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}>
                        <Icon icon="mdi:view-grid-outline" className="tw-text-base" />
                    </button>
                    <button onClick={() => changeView('list')} title="List view"
                        className={`tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded ${viewMode === 'list' ? 'tw-bg-white tw-text-blue-600 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}>
                        <Icon icon="mdi:view-list-outline" className="tw-text-base" />
                    </button>
                </div>
            </div>

            {/* Loading */}
            {projectsListLoading && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-12">
                    <Icon icon="mdi:loading" className="tw-text-4xl tw-text-blue-500 tw-animate-spin" />
                    <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">Loading projects...</p>
                </div>
            )}

            {/* Grid */}
            {!projectsListLoading && (
                projects.length === 0 ? (
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-16">
                        <div className="tw-w-14 tw-h-14 tw-mx-auto tw-rounded-full tw-bg-gray-50 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="hugeicons:folder-01" className="tw-text-3xl tw-text-gray-300" />
                        </div>
                        <p className="tw-text-gray-700 tw-mt-3 tw-text-sm tw-font-medium">No projects yet</p>
                        <p className="tw-text-gray-400 tw-text-xs tw-mt-1">Create a project to group related tasks together.</p>
                        {canManage && (
                            <button onClick={openCreate} className="tw-mt-4 tw-inline-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700">
                                <Icon icon="mdi:plus" /> New Project
                            </button>
                        )}
                    </div>
                ) : viewMode === 'card' ? (
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-3">
                        {projects.map((p) => {
                            const empName = p.employee ? `${p.employee.first_name || ''} ${p.employee.last_name || ''}`.trim() : '';
                            const empInitials = p.employee ? `${(p.employee.first_name || '').charAt(0)}${(p.employee.last_name || '').charAt(0)}`.toUpperCase() : '';
                            return (
                                <div key={p.project_id}
                                    onClick={() => navigate(`/society-portal/tasks/projects/${p.project_id}`)}
                                    className="tw-group tw-bg-white tw-rounded-lg tw-border tw-border-gray-100 tw-shadow-sm hover:tw-shadow-md hover:tw-border-blue-200 tw-transition-all tw-cursor-pointer tw-flex tw-flex-col tw-overflow-hidden">
                                    <div className="tw-h-0.5 tw-bg-gradient-to-r tw-from-blue-500 tw-to-indigo-500" />
                                    <div className="tw-p-3 tw-flex tw-flex-col tw-flex-1">
                                        {/* Header */}
                                        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                                            <div className="tw-w-8 tw-h-8 tw-rounded-md tw-bg-gradient-to-br tw-from-blue-500 tw-to-indigo-500 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                <Icon icon="hugeicons:folder-01" className="tw-text-white tw-text-sm" />
                                            </div>
                                            <div className="tw-min-w-0 tw-flex-1">
                                                <p className="tw-font-semibold tw-text-gray-800 tw-text-[13px] tw-leading-tight tw-line-clamp-1 group-hover:tw-text-blue-600 tw-transition-colors">{p.name}</p>
                                            </div>
                                            <span className={`tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[9px] tw-font-medium tw-flex-shrink-0 ${projectStatusBadgeClass(p.status)}`}>{humanize(p.status)}</span>
                                        </div>

                                        {/* Description */}
                                        <p className="tw-mt-0 tw-mb-3 tw-text-[13px] tw-text-gray-600 tw-leading-snug tw-line-clamp-2 tw-min-h-[34px]">{p.description || 'No description'}</p>

                                        {/* Timeline */}
                                        <div className="tw-mb-3">
                                            <p className="tw-m-0 tw-mb-1 tw-text-[10px] tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wide">Timeline</p>
                                            <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-gray-700">
                                                <Icon icon="mdi:calendar-blank-outline" className="tw-text-gray-400" />
                                                <span>{fmtDate(p.start_date)}</span>
                                                <Icon icon="mdi:arrow-right-thin" className="tw-text-gray-300" />
                                                <span>{fmtDate(p.due_date)}</span>
                                            </div>
                                        </div>

                                        {/* Footer: employee + actions */}
                                        <div className="tw-mt-auto tw-pt-2 tw-border-t tw-border-gray-100">
                                            {/* Employee */}
                                            {p.employee ? (
                                                <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-mb-2">
                                                    <span className="tw-w-9 tw-h-9 tw-rounded-full tw-bg-blue-100 tw-text-blue-700 tw-text-xs tw-font-bold tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">{empInitials || '?'}</span>
                                                    <div className="tw-min-w-0 tw-leading-tight">
                                                        <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-gray-800 tw-truncate">{empName}</p>
                                                        <p className="tw-m-0 tw-text-xs tw-font-medium tw-text-gray-500 tw-truncate">{p.employee.designation || 'Employee'}</p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-text-gray-400 tw-mb-2">
                                                    <span className="tw-w-9 tw-h-9 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                        <Icon icon="mdi:account-outline" />
                                                    </span>
                                                    <span className="tw-text-sm tw-font-semibold">Unassigned</span>
                                                </div>
                                            )}
                                            {/* Actions */}
                                            <div className="tw-flex tw-items-stretch tw-gap-1.5" onClick={(e) => e.stopPropagation()}>
                                                <button onClick={() => navigate(`/society-portal/tasks/projects/${p.project_id}`)} className="tw-flex-1 tw-h-8 tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-blue-600 tw-bg-blue-50 hover:tw-bg-blue-100 tw-rounded-md tw-transition-colors">
                                                    <Icon icon="mdi:format-list-checks" className="tw-text-sm" /> View Tasks
                                                </button>
                                                {canManage && (
                                                    <>
                                                        <button onClick={() => openEdit(p)} className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-gray-200 tw-text-gray-400 hover:tw-bg-gray-50 hover:tw-text-gray-600 tw-flex-shrink-0" title="Edit">
                                                            <Icon icon="mdi:pencil-outline" className="tw-text-sm" />
                                                        </button>
                                                        <button onClick={() => setToDelete(p)} className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-gray-200 tw-text-gray-400 hover:tw-bg-red-50 hover:tw-text-red-500 hover:tw-border-red-200 tw-flex-shrink-0" title="Delete">
                                                            <Icon icon="mdi:delete-outline" className="tw-text-sm" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    /* List view */
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
                        <div className="tw-overflow-x-auto">
                            <table className="tw-w-full tw-text-sm">
                                <thead>
                                    <tr className="tw-bg-gray-50 tw-text-left tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide">
                                        <th className="tw-px-4 tw-py-2.5">Project</th>
                                        <th className="tw-px-4 tw-py-2.5">Status</th>
                                        <th className="tw-px-4 tw-py-2.5">Assigned To</th>
                                        <th className="tw-px-4 tw-py-2.5">Timeline</th>
                                        <th className="tw-px-4 tw-py-2.5 tw-text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="tw-divide-y tw-divide-gray-100">
                                    {projects.map((p) => {
                                        const empName = p.employee ? `${p.employee.first_name || ''} ${p.employee.last_name || ''}`.trim() : '';
                                        const empInitials = p.employee ? `${(p.employee.first_name || '').charAt(0)}${(p.employee.last_name || '').charAt(0)}`.toUpperCase() : '';
                                        return (
                                            <tr key={p.project_id}
                                                onClick={() => navigate(`/society-portal/tasks/projects/${p.project_id}`)}
                                                className="tw-cursor-pointer hover:tw-bg-blue-50/50 tw-transition-colors">
                                                <td className="tw-px-4 tw-py-2.5">
                                                    <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                                                        <div className="tw-w-8 tw-h-8 tw-rounded-md tw-bg-gradient-to-br tw-from-blue-500 tw-to-indigo-500 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                            <Icon icon="hugeicons:folder-01" className="tw-text-white tw-text-sm" />
                                                        </div>
                                                        <div className="tw-min-w-0">
                                                            <p className="tw-m-0 tw-font-semibold tw-text-gray-800 tw-truncate">{p.name}</p>
                                                            <p className="tw-m-0 tw-text-xs tw-text-gray-500 tw-truncate tw-max-w-[240px]">{p.description || 'No description'}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="tw-px-4 tw-py-2.5">
                                                    <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${projectStatusBadgeClass(p.status)}`}>{humanize(p.status)}</span>
                                                </td>
                                                <td className="tw-px-4 tw-py-2.5">
                                                    {p.employee ? (
                                                        <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                                                            <span className="tw-w-7 tw-h-7 tw-rounded-full tw-bg-blue-100 tw-text-blue-700 tw-text-[11px] tw-font-bold tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">{empInitials || '?'}</span>
                                                            <span className="tw-text-gray-700 tw-font-medium tw-truncate">{empName}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="tw-text-gray-400">Unassigned</span>
                                                    )}
                                                </td>
                                                <td className="tw-px-4 tw-py-2.5 tw-whitespace-nowrap">
                                                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600">
                                                        <span>{fmtDate(p.start_date)}</span>
                                                        <Icon icon="mdi:arrow-right-thin" className="tw-text-gray-300" />
                                                        <span>{fmtDate(p.due_date)}</span>
                                                    </div>
                                                </td>
                                                <td className="tw-px-4 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
                                                    <div className="tw-flex tw-items-center tw-justify-end tw-gap-1.5">
                                                        <button onClick={() => navigate(`/society-portal/tasks/projects/${p.project_id}`)} className="tw-h-7 tw-px-2 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-blue-600 tw-bg-blue-50 hover:tw-bg-blue-100 tw-rounded-md tw-transition-colors" title="View Tasks">
                                                            <Icon icon="mdi:format-list-checks" className="tw-text-sm" /> Tasks
                                                        </button>
                                                        {canManage && (
                                                            <>
                                                                <button onClick={() => openEdit(p)} className="tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-gray-200 tw-text-gray-400 hover:tw-bg-gray-50 hover:tw-text-gray-600 tw-flex-shrink-0" title="Edit">
                                                                    <Icon icon="mdi:pencil-outline" className="tw-text-sm" />
                                                                </button>
                                                                <button onClick={() => setToDelete(p)} className="tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-gray-200 tw-text-gray-400 hover:tw-bg-red-50 hover:tw-text-red-500 hover:tw-border-red-200 tw-flex-shrink-0" title="Delete">
                                                                    <Icon icon="mdi:delete-outline" className="tw-text-sm" />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )
            )}

            {/* Create / Edit modal */}
            {modalOpen && (
                <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-lg tw-mx-4">
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                            <h3 className="tw-text-base tw-font-semibold tw-text-gray-800">{editing ? 'Edit Project' : 'New Project'}</h3>
                            <button onClick={() => setModalOpen(false)} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400"><Icon icon="mdi:close" /></button>
                        </div>
                        <div className="tw-px-5 tw-py-4 tw-space-y-4">
                            <div>
                                <label className={labelCls}>Name <span className="tw-text-red-500">*</span></label>
                                <input type="text" maxLength={150} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Monsoon Prep 2026" className={inputCls} />
                                {attempted && !form.name.trim() && <p className="tw-text-[11px] tw-text-red-500 tw-mt-1">Name is required</p>}
                            </div>
                            <div>
                                <label className={labelCls}>Description</label>
                                <textarea rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} className={inputCls} />
                            </div>
                            <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                                <div>
                                    <label className={labelCls}>Status</label>
                                    <SearchableDropdown value={form.status} onChange={(v) => set('status', v === 'all' ? 'ACTIVE' : v)}
                                        options={statusOptions} placeholder="Select status" clearable={false} />
                                </div>
                                <div>
                                    <label className={labelCls}>Employee</label>
                                    <SearchableDropdown value={form.employee_id} onChange={(v) => set('employee_id', v === 'all' ? '' : v)}
                                        options={employeeOptions} loading={employeesDropdownLoading} placeholder="Select employee" />
                                </div>
                                <div>
                                    <label className={labelCls}>Start Date</label>
                                    <input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Due Date</label>
                                    <input type="date" value={form.due_date} onChange={(e) => set('due_date', e.target.value)} className={inputCls} />
                                </div>
                            </div>
                        </div>
                        <div className="tw-flex tw-justify-end tw-gap-2 tw-px-5 tw-py-3 tw-border-t tw-border-gray-100">
                            <button onClick={() => setModalOpen(false)} disabled={saving} className="tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg disabled:tw-opacity-50">Cancel</button>
                            <button onClick={handleSave} disabled={saving} className="tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-lg tw-flex tw-items-center tw-gap-1.5 disabled:tw-opacity-50">
                                {saving ? <><Icon icon="mdi:loading" className="tw-animate-spin" /> Saving...</> : <><Icon icon="mdi:content-save-outline" /> {editing ? 'Save' : 'Create'}</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete modal */}
            {toDelete && (
                <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-p-6 tw-max-w-md tw-w-full tw-mx-4">
                        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                            <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-red-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                <Icon icon="mdi:delete-alert" className="tw-text-red-600 tw-text-xl" />
                            </div>
                            <div>
                                <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800">Delete Project</h3>
                                <p className="tw-text-sm tw-text-gray-500">Tasks will be detached, not deleted</p>
                            </div>
                        </div>
                        <p className="tw-text-sm tw-text-gray-600 tw-mb-6">Delete <span className="tw-font-semibold">{toDelete.name}</span>?</p>
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                            <button onClick={() => setToDelete(null)} disabled={deleteProjectLoading} className="tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg disabled:tw-opacity-50">Cancel</button>
                            <button onClick={handleDelete} disabled={deleteProjectLoading} className="tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-red-600 hover:tw-bg-red-700 tw-rounded-lg tw-flex tw-items-center tw-gap-2 disabled:tw-opacity-50">
                                {deleteProjectLoading ? <><Icon icon="mdi:loading" className="tw-animate-spin" /> Deleting...</> : <><Icon icon="mdi:delete" /> Delete</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Projects;
